import { useState } from 'react';
import { count } from './adminData.js';
import { niceTicks } from './chartData.js';

// The overview's charts, drawn in plain HTML so the text stays crisp at any width. Marks take the chart colours (--viz-1, --viz-2,
// --viz-3: heather, sage and amber, stepped for dark mode); words never do. Each chart carries its numbers as a table for screen readers.

/** The key to which colour is which: a small swatch the shape of the mark, then the name. Only drawn for two or more series. */
function Legend({ series }) {
  return (
    <ul className="viz-legend">
      {series.map((item) => <li key={item.key}><span className="viz-key" style={{ background: item.color }}/>{item.label}</li>)}
    </ul>
  );
}

/** What a column holds, on hover or focus: the week, then each series' number (the number first) beside a short line of its colour. */
function Tooltip({ title, rows, edge }) {
  return (
    <div className={`viz-tooltip${edge ? ` is-${edge}` : ''}`} role="presentation">
      <p className="viz-tooltip-title">{title}</p>
      {rows.map((row) => (
        <p key={row.label} className="viz-tooltip-row">
          <span className="viz-line-key" style={{ background: row.color }}/>
          <strong>{count(row.value)}</strong> {row.label}
        </p>
      ))}
    </div>
  );
}

/** The chart's numbers as a table, for screen readers. */
function DataTable({ caption, head, rows }) {
  return (
    <table className="visually-hidden">
      <caption>{caption}</caption>
      <thead><tr>{head.map((cell) => <th key={cell} scope="col">{cell}</th>)}</tr></thead>
      <tbody>{rows.map((row) => <tr key={row[0]}>{row.map((cell, index) => (index === 0 ? <th key={index} scope="row">{cell}</th> : <td key={index}>{cell}</td>))}</tr>)}</tbody>
    </table>
  );
}

/**
 * Columns by week: one series, or two side by side (with a legend). `series` is `[{ key, label, color }]` and `weeks` is
 * `[{ label, counts: { key: number } }]`, oldest first. Hovering or focusing a week shows every series' number for it.
 */
export function WeekColumns({ title, series, weeks }) {
  const [active, setActive] = useState(-1);
  const valueOf = (week, item) => week.counts[item.key] || 0;
  const ticks = niceTicks(Math.max(0, ...weeks.flatMap((week) => series.map((item) => valueOf(week, item)))));
  const top = ticks.at(-1);
  // Every other week's date under the axis when there are many, so they never run into each other; the newest always.
  const every = weeks.length > 8 ? 2 : 1;
  return (
    <figure className="viz" aria-label={title}>
      {series.length > 1 && <Legend series={series}/>}
      <div className="viz-plot">
        <div className="viz-grid" aria-hidden="true">
          {ticks.map((tick) => <span key={tick} className="viz-tick" style={{ bottom: `${(tick / top) * 100}%` }}><span>{count(tick)}</span></span>)}
        </div>
        <div className="viz-columns">
          {weeks.map((week, index) => (
            <div
              key={week.label}
              className={`viz-band${active === index ? ' is-active' : ''}`}
              tabIndex={0}
              aria-label={`Week of ${week.label}: ${series.map((item) => `${valueOf(week, item)} ${item.label.toLowerCase()}`).join(', ')}`}
              onPointerEnter={() => setActive(index)}
              onPointerLeave={() => setActive(-1)}
              onFocus={() => setActive(index)}
              onBlur={() => setActive(-1)}
            >
              <div className="viz-bars">
                {series.map((item) => {
                  const value = valueOf(week, item);
                  return <span key={item.key} className="viz-col" style={{ height: value ? `max(2px, ${(value / top) * 100}%)` : 0, background: item.color }}/>;
                })}
              </div>
              {active === index && (
                <Tooltip
                  title={`Week of ${week.label}`}
                  rows={series.map((item) => ({ label: item.label, color: item.color, value: valueOf(week, item) }))}
                  edge={index === 0 ? 'first' : index === weeks.length - 1 ? 'last' : ''}
                />
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="viz-xaxis" aria-hidden="true">
        {weeks.map((week, index) => <span key={week.label}>{index % every === (weeks.length - 1) % every ? week.label : ''}</span>)}
      </div>
      <DataTable caption={title} head={['Week of', ...series.map((item) => item.label)]} rows={weeks.map((week) => [week.label, ...series.map((item) => valueOf(week, item))])}/>
    </figure>
  );
}

/**
 * Bars across, one per row, longest for the largest, each with its number at the tip and an optional quieter `note` after it.
 * `rows` is `[{ key, label, value, note?, onOpen? }]`; a row with `onOpen` is a button that opens what it counts.
 */
export function Bars({ title, rows, color = 'var(--viz-1)' }) {
  const top = Math.max(1, ...rows.map((row) => row.value || 0));
  return (
    <figure className="viz viz-hbars" aria-label={title}>
      {rows.map((row) => {
        // The bar's share of the track, after room is kept at the end for its number (and note).
        const content = (
          <>
            <span className="viz-hlabel">{row.label}</span>
            <span className={`viz-htrack${row.note ? ' has-note' : ''}`}>
              <span className={`viz-hbar${row.value ? '' : ' is-zero'}`} style={{ '--share': (row.value || 0) / top, background: color }}/>
              <span className="viz-hvalue">{count(row.value || 0)}</span>
              {row.note && <span className="viz-hnote">{row.note}</span>}
            </span>
          </>
        );
        return row.onOpen
          ? <button key={row.key} type="button" className="viz-hrow" onClick={row.onOpen} aria-label={`${row.label}, ${count(row.value || 0)}${row.note ? `, ${row.note}` : ''}`}>{content}</button>
          : <div key={row.key} className="viz-hrow">{content}</div>;
      })}
      <DataTable caption={title} head={['', 'Number']} rows={rows.map((row) => [row.label, row.value || 0])}/>
    </figure>
  );
}
